import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const DEFAULT_WIDTH = 320;
const DEFAULT_HEIGHT = 360;
const EDGE_PADDING = 12;
const STARTER_PROMPTS = ['Live music tonight', 'Affordable dinner spots', 'Where is the vibe right now?'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getFallbackPosition = () => {
  if (typeof window === 'undefined') {
    return { x: EDGE_PADDING, y: 100 };
  }

  return {
    x: Math.max(EDGE_PADDING, window.innerWidth - DEFAULT_WIDTH - EDGE_PADDING),
    y: Math.max(95, window.innerHeight - DEFAULT_HEIGHT - 120)
  };
};

const isMetaButton = (target) => {
  if (!target || typeof target.closest !== 'function') {
    return false;
  }
  return !!target.closest('button');
};

const MapChatbot = ({
  messages,
  loading,
  error,
  position,
  collapsed,
  suggestions,
  onSend,
  onClear,
  onClearHighlights,
  onHide,
  onSelectSuggestion,
  onPositionChange,
  onCollapsedChange
}) => {
  const [draft, setDraft] = useState('');
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const didDragRef = useRef(false);
  const panelRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastPositionRef = useRef(position || getFallbackPosition());

  const currentPosition = (
    position && Number.isFinite(position.x) && Number.isFinite(position.y)
      ? position
      : getFallbackPosition()
  );

  useEffect(() => {
    lastPositionRef.current = currentPosition;
  }, [currentPosition]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ block: 'end' });
    }
  }, [messages, loading, error, collapsed]);

  useEffect(() => {
    if (!dragging) {
      return undefined;
    }

    const handleMouseMove = (event) => {
      didDragRef.current = true;
      const panelWidth = (panelRef.current ? panelRef.current.offsetWidth : DEFAULT_WIDTH);
      const panelHeight = (panelRef.current ? panelRef.current.offsetHeight : DEFAULT_HEIGHT);
      const minY = 60;
      const maxX = Math.max(EDGE_PADDING, window.innerWidth - panelWidth - EDGE_PADDING);
      const maxY = Math.max(minY, window.innerHeight - panelHeight - EDGE_PADDING);

      const nextPosition = {
        x: clamp(event.clientX - dragOffsetRef.current.x, EDGE_PADDING, maxX),
        y: clamp(event.clientY - dragOffsetRef.current.y, minY, maxY)
      };

      lastPositionRef.current = nextPosition;
      if (onPositionChange) {
        onPositionChange(nextPosition, false);
      }
    };

    const handleMouseUp = () => {
      setDragging(false);
      if (onPositionChange) {
        onPositionChange(lastPositionRef.current, true);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, onPositionChange]);

  const handleHeaderMouseDown = (event) => {
    if (event.button !== 0 || isMetaButton(event.target)) {
      return;
    }
    if (!panelRef.current) {
      return;
    }

    didDragRef.current = false;
    const rect = panelRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    setDragging(true);
  };

  const handleHeaderClick = (event) => {
    if (isMetaButton(event.target)) {
      return;
    }
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    if (onHide) {
      onHide();
      return;
    }

    if (onCollapsedChange) {
      onCollapsedChange(true);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value || loading) {
      return;
    }

    if (onSend) {
      onSend(value);
    }
    setDraft('');
  };

  const handleStarterPrompt = (prompt) => {
    if (loading || !onSend) {
      return;
    }
    onSend(prompt);
  };

  return (
    <div
      ref={panelRef}
      className={'map-chatbot-shell' + (dragging ? ' is-dragging' : '') + (collapsed ? ' is-collapsed' : '')}
      style={{ left: currentPosition.x, top: currentPosition.y }}
    >
      <div className="map-chatbot-header" onMouseDown={handleHeaderMouseDown} onClick={handleHeaderClick}>
        <div className="map-chatbot-header-left">
          <div className="map-chatbot-avatar">G</div>
          <div className="map-chatbot-title-wrap">
            <div className="map-chatbot-title">Gino</div>
            <div className="map-chatbot-subtitle">Your local vibe finder</div>
          </div>
        </div>
        <div className="map-chatbot-header-actions">
          <button
            type="button"
            className="map-chatbot-header-btn"
            onClick={() => { if (onClear) { onClear(); } }}
          >
            Reset
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="map-chatbot-messages">
          {/* {(!messages || messages.length === 0) && (
            <div className="map-chatbot-placeholder">
              Tell me your mood and I will find spots and events nearby.
            </div>
          )} */}

          {(!messages || messages.length === 0) && !loading && (
            <div className="map-chatbot-chip-row">
              {STARTER_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="map-chatbot-chip"
                  onClick={() => { handleStarterPrompt(prompt); }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          {messages && messages.map((entry) => (
            <div
              key={entry.id}
              className={'map-chatbot-message ' + (entry.role === 'user' ? 'is-user' : 'is-assistant')}
            >
              <div className="map-chatbot-bubble">{entry.content}</div>
            </div>
          ))}

          {loading && (
            <div className="map-chatbot-message is-assistant">
              <div className="map-chatbot-bubble map-chatbot-typing">
                <span className="map-chatbot-typing-label">Gino is thinking</span>
                <span className="map-chatbot-typing-dots"><i></i><i></i><i></i></span>
              </div>
            </div>
          )}

          {error && <div className="map-chatbot-error">{error}</div>}

          {Array.isArray(suggestions) && suggestions.length > 0 && (
            <div className="map-chatbot-suggestions">
              <div className="map-chatbot-suggestions-title">Suggested on map</div>
              <div className="map-chatbot-suggestions-list">
                {suggestions.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className="map-chatbot-suggestion-btn"
                    onClick={() => { if (onSelectSuggestion) { onSelectSuggestion(entry.id); } }}
                  >
                    <span className="map-chatbot-suggestion-name">{entry.title}</span>
                    {entry.subtitle && <span className="map-chatbot-suggestion-meta">{entry.subtitle}</span>}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="map-chatbot-suggestions-clear"
                onClick={() => { if (onClearHighlights) { onClearHighlights(); } }}
              >
                Clear map highlights
              </button>
            </div>
          )}

          <form className="map-chatbot-composer" onSubmit={handleSubmit}>
            <input
              type="text"
              className="map-chatbot-input"
              value={draft}
              placeholder="Tell Gino what vibe you want..."
              onChange={(event) => { setDraft(event.target.value); }}
              disabled={loading}
            />
            <button type="submit" className="map-chatbot-send" disabled={loading || draft.trim().length === 0}>
              <FontAwesomeIcon icon={"paper-plane"} />
            </button>
          </form>
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
};

export default MapChatbot;
